import { graphql, Link, useStaticQuery } from "gatsby";
import React, { useEffect, useState } from "react";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import { GatsbyImage } from "gatsby-plugin-image";
import parse from "html-react-parser";

import logoLight from "../../../images/logo-light.svg";

import * as s from "./header.module.css";

import InsuranceFormPopup from "../../InsuranceFormPopup/InsuranceFormPopup";
import MobileNav from "./mobile-nav";

const Header = (withoutBg) => {
  const bp = useBreakpoint();

  // hydration-safe breakpoint handling (SSR defaults to desktop)
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const isXL = hydrated ? !!bp?.xl : true;

  const [opened, setOpened] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [showMobileCta, setShowMobileCta] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsSticky(true);
        setShowMobileCta(true);
      } else {
        setIsSticky(false);
        setShowMobileCta(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (opened) {
      document.body.style.overflow = "hidden";
      const footer = document.querySelector("footer");
      if (footer) footer.style.opacity = 0;
    } else {
      document.body.style.overflow = "";
      const footer = document.querySelector("footer");
      if (footer) footer.style.opacity = 1;
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [opened]);

  const {
    cta_menu: { ctaMenuItems },
    acfOptionsHeader: { acfOptionsHeader },
  } = useStaticQuery(graphql`
    query HeaderMenuQuery {
      acfOptionsHeader: wp {
        acfOptionsHeader {
          headerMenu {
            ctaMobile
            menuItems {
              childItems {
                itemContent {
                  description
                  title
                  image {
                    localFile {
                      childImageSharp {
                        gatsbyImageData(quality: 100)
                      }
                    }
                  }
                  textColor
                }
                link {
                  title
                  url
                  target
                }
              }
              isListed
              hierarchyDepth
              itemContent {
                description
                title
                image {
                  localFile {
                    childImageSharp {
                      gatsbyImageData(quality: 100)
                    }
                  }
                }
                textColor
              }
              parentItem {
                showAsDefault
                link {
                  target
                  title
                  url
                }
              }
              childItemsThree {
                parentLink {
                  url
                  title
                  target
                }
                dontShowChild
                childs {
                  link {
                    url
                    title
                    target
                  }
                  itemContent {
                    description
                    title
                    image {
                      localFile {
                        childImageSharp {
                          gatsbyImageData(quality: 100)
                        }
                      }
                    }
                    textColor
                  }
                }
              }
            }
          }
          insuranceFirms {
            insuranceFirms {
              text
            }
          }
        }
      }
      cta_menu: wpMenu(locations: { eq: HEADER_CTA_MENU }) {
        ctaMenuItems: menuItems {
          nodes {
            id
            uri
            title
            target
            label
            linkRelationship
            cssClasses
            parentId
            connectedNode {
              node {
                ... on WpPage {
                  uri
                  title
                }
              }
            }
          }
        }
      }
    }
  `);

  const flatListToHierarchical = (
    data = [],
    { idKey = "id", parentKey = "parentId", childrenKey = "items" } = {}
  ) => {
    const tree = [];
    const childrenOf = {};
    data.forEach((item) => {
      const newItem = { ...item };
      const { [idKey]: id, [parentKey]: parentId = 0 } = newItem;
      childrenOf[id] = childrenOf[id] || [];
      newItem[childrenKey] = childrenOf[id];
      parentId
        ? (childrenOf[parentId] = childrenOf[parentId] || []).push(newItem)
        : tree.push(newItem);
    });
    return tree;
  };

  let wpMenuItemsTree = acfOptionsHeader?.headerMenu?.menuItems;
  let ctaMenuItemsTree = flatListToHierarchical(ctaMenuItems.nodes);

  // 👇 NEW: logo size via isXL
  const logoWidth = isXL ? 140 : 109;
  const logoHeight = isXL ? 75 : 59;

  return (
    <div
      className={`w-100 ${s.headerWrapper} ${
        !!opened || !!isSticky || withoutBg.withoutBg ? s.sticky : ""
      }`}
    >
      <InsuranceFormPopup block={acfOptionsHeader?.insuranceFirms} />

      {/* Mobile CTA */}
      {!isXL && (
        <div className={s.ctaMenuMobile}>
          <div className={`${s.ctaContent} ${showMobileCta ? s.visible : ""}`}>
            {parse(acfOptionsHeader?.headerMenu?.ctaMobile)}
          </div>
        </div>
      )}

      <header className={`${s.header}`}>
        <div className="h-100 container position-relative">
          <div className="h-100 d-flex justify-content-between align-items-center">
            <div
              className={s.menuToggler}
              onClick={() => {
                setOpened(!opened);
              }}
            >
              {!!opened ? (
                <svg
                  width="37"
                  height="36"
                  viewBox="0 0 37 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="10.3682"
                    y="8.45404"
                    width="25"
                    height="2"
                    rx="1"
                    transform="rotate(45 10.3682 8.45404)"
                    fill="#1F316F"
                  />
                  <rect
                    x="8.9541"
                    y="26.1317"
                    width="25"
                    height="2"
                    rx="1"
                    transform="rotate(-45 8.9541 26.1317)"
                    fill="#1F316F"
                  />
                </svg>
              ) : (
                <svg
                  width="25"
                  height="14"
                  viewBox="0 0 25 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="25"
                    height="2"
                    rx="1"
                    fill={
                      isSticky || withoutBg.withoutBg ? "#1F316F" : "#F7F7F8"
                    }
                  />
                  <rect
                    y="6"
                    width="25"
                    height="2"
                    rx="1"
                    fill={
                      isSticky || withoutBg.withoutBg ? "#1F316F" : "#F7F7F8"
                    }
                  />
                  <rect
                    y="12"
                    width="25"
                    height="2"
                    rx="1"
                    fill={
                      isSticky || withoutBg.withoutBg ? "#1F316F" : "#F7F7F8"
                    }
                  />
                </svg>
              )}
            </div>
            {!isSticky && !opened && (
              <div className={s.absoluteCentered}>
                <Link to="/">
                  <img
                    alt="logo"
                    width={logoWidth}
                    height={logoHeight}
                    src={
                      withoutBg?.withoutBg ? "/images/logo-dark.svg" : logoLight
                    }
                    loading="eager"
                    className="d-block"
                  />
                </Link>
              </div>
            )}
            {(!!isSticky || !!opened) && (
              <div className={s.absoluteCentered}>
                <Link to="/">
                  <img
                    alt="logo"
                    width={logoWidth}
                    height={logoHeight}
                    src={
                      withoutBg?.withoutBg
                        ? "/images/logo-dark.svg"
                        : "/images/logo-dark.svg"
                    }
                    loading="eager"
                    className={`d-block`}
                  />
                </Link>
              </div>
            )}

            {/* Desktop CTA menu */}
            {isXL && (
              <div className={s.ctaMenu}>
                {ctaMenuItemsTree?.map((mi, i) => {
                  const isLastItem = i === ctaMenuItemsTree.length - 1;
                  return (
                    <div key={i}>
                      {!!mi.target && mi.target === "_blank" && (
                        <a
                          href={mi.uri}
                          rel="noreferrer"
                          target="_blank"
                          className={`globalButton ctm-no-swap ${s.CtaButton} ${
                            isLastItem ? s.CtaButtonLast : s.ctaButtonFirst
                          }`}
                        >
                          {mi?.cssClasses &&
                            mi?.cssClasses[0] == "call-btn" && (
                              <svg
                                className={s.callIcon}
                                width="13"
                                height="14"
                                viewBox="0 0 13 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M11.9005 10.4287C11.9005 10.6237 11.8571 10.8241 11.765 11.0191C11.673 11.2141 11.5538 11.3983 11.3967 11.5716C11.1313 11.8641 10.8388 12.0753 10.5084 12.2108C10.1834 12.3462 9.83129 12.4166 9.45212 12.4166C8.89962 12.4166 8.30921 12.2866 7.68629 12.0212C7.06337 11.7558 6.44046 11.3983 5.82296 10.9487C5.20004 10.4937 4.60962 9.98992 4.04629 9.432C3.48837 8.86867 2.98462 8.27825 2.53504 7.66075C2.09087 7.04325 1.73337 6.42575 1.47337 5.81367C1.21337 5.19617 1.08337 4.60575 1.08337 4.04242C1.08337 3.67409 1.14837 3.322 1.27837 2.997C1.40837 2.66659 1.61421 2.36325 1.90129 2.09242C2.24796 1.75117 2.62712 1.58325 3.02796 1.58325C3.17962 1.58325 3.33129 1.61575 3.46671 1.68075C3.60754 1.74575 3.73212 1.84325 3.82962 1.98409L5.08629 3.75534C5.18379 3.89075 5.25421 4.01534 5.30296 4.1345C5.35171 4.24825 5.37879 4.362 5.37879 4.46492C5.37879 4.59492 5.34087 4.72492 5.26504 4.8495C5.19462 4.97408 5.09171 5.10408 4.96171 5.23408L4.55004 5.662C4.49046 5.72158 4.46337 5.792 4.46337 5.87867C4.46337 5.922 4.46879 5.95992 4.47962 6.00325C4.49587 6.04658 4.51212 6.07908 4.52296 6.11158C4.62046 6.29033 4.78837 6.52325 5.02671 6.80492C5.27046 7.08659 5.53046 7.37367 5.81212 7.66075C6.10462 7.94783 6.38629 8.21325 6.67337 8.457C6.95504 8.69533 7.18796 8.85783 7.37212 8.95533C7.39921 8.96617 7.43171 8.98242 7.46962 8.99867C7.51296 9.01492 7.55629 9.02033 7.60504 9.02033C7.69712 9.02033 7.76754 8.98783 7.82712 8.92825L8.23879 8.522C8.37421 8.38659 8.50421 8.28367 8.62879 8.21867C8.75337 8.14283 8.87796 8.10492 9.01337 8.10492C9.11629 8.10492 9.22462 8.12658 9.34379 8.17533C9.46296 8.22408 9.58754 8.2945 9.72296 8.38659L11.5159 9.6595C11.6567 9.757 11.7542 9.87075 11.8138 10.0062C11.868 10.1416 11.9005 10.277 11.9005 10.4287Z"
                                  stroke="#F8EDE3"
                                  strokeMiterlimit="10"
                                />
                                <path
                                  d="M10.0208 5.37484C10.0208 5.04984 9.76625 4.5515 9.38708 4.14525C9.04042 3.7715 8.58 3.479 8.125 3.479"
                                  stroke="#F8EDE3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M11.9167 5.37492C11.9167 3.27867 10.2213 1.58325 8.125 1.58325"
                                  stroke="#F8EDE3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          {mi.label}
                        </a>
                      )}
                      {!mi.target && (
                        <Link
                          to={mi.uri}
                          className={`globalButton ${s.CtaButton} ${
                            isLastItem ? s.CtaButtonLast : s.ctaButtonFirst
                          }`}
                        >
                          {mi.label}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {/* Mobile menu */}
        {!isXL && <MobileNav menuItems={wpMenuItemsTree} toggled={opened} />}
      </header>
      {/* Desktop mega menu */}
      {isXL && (
        <MenuWrapper wpMenuItemsTree={wpMenuItemsTree} opened={opened} />
      )}
    </div>
  );
};

export default Header;

const ItemContent = ({ block }) => {
  const image = {
    data: block?.image?.localFile?.childImageSharp?.gatsbyImageData,
    alt: block?.image?.altText || "",
  };

  const [classes, setClasses] = useState(s.initing);
  const [timeout1, setTimeout1] = useState(null);
  const [timeout2, setTimeout2] = useState(null);
  useEffect(() => {
    setClasses("");
    clearInterval(timeout1);
    clearInterval(timeout2);
    let t1 = setTimeout(function () {
      setClasses(`${s.fading} ${s.active}`);
      let t2 = setTimeout(function () {
        setClasses(`${s.active}`);
      }, 500);
      setTimeout2(t2);
    }, 10);
    setTimeout1(t1);
  }, [block]);

  return (
    <div className={`${s.itemContentWrapper} ${classes}`}>
      {!!image.data && (
        <div className={s.contentBgIamge}>
          <GatsbyImage
            placeholder="#fefefe"
            loading="eager"
            className={`w-100 h-100`}
            image={image.data}
            alt={image.alt}
            objectFit="cover"
            objectPosition="center"
          />
        </div>
      )}
      <div className={s.itemContentTextWrapper}>
        {block?.title && (
          <p
            className={s.itemContentTextWrapperTitle}
            style={{ color: block?.textColor }}
          >
            {block.title}
          </p>
        )}
        {block?.description && (
          <p style={{ color: block?.textColor }}>{block.description}</p>
        )}
      </div>
    </div>
  );
};

const MenuWrapper = ({ wpMenuItemsTree, opened }) => {
  const [currentItem, setCurrentItem] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [activeListItem, setActiveListItem] = useState(null);
  const [activeChildItem, setActiveChildItem] = useState(null);
  const listedItems = wpMenuItemsTree.filter((val) => val.isListed);
  const navItems = wpMenuItemsTree.filter((val) => !val.isListed);
  const defaultItem = navItems.find((item) => item?.parentItem?.showAsDefault);
  const handleMouseEnterNav = (key) => {
    setActiveItem(key);
  };

  const handleMouseLeaveNav = () => {
    setActiveItem(null);
  };

  const handleMouseEnterList = (key, firstChildKey) => {
    setActiveListItem(key);
    setActiveChildItem(firstChildKey);
  };

  const handleMouseLeaveList = () => {
    setActiveListItem(null);
    setActiveChildItem(null);
    setActiveItem(null);
  };

  const handleMouseEnterChild = (childKey) => {
    setActiveChildItem(childKey);
  };
  return (
    <div className={`${s.menuWrapper} ${opened ? s.opened : ""}`}>
      <div className={s.listedItemsWrapper}>
        <ul className={s.listedItemsList}>
          {listedItems.map((item, key) => {
            const firstChildKey = item?.childItems?.length ? 0 : null;
            return (
              <li
                className={`${s.listParentItem} ${
                  activeListItem === key ? s.active : ""
                }`}
                key={key}
                onMouseEnter={() => {
                  handleMouseEnterList(key, firstChildKey);
                  if (item?.hierarchyDepth === "three") {
                    setCurrentItem(item?.childItemsThree[0]?.childs[0]);
                  }
                }}
                onMouseLeave={handleMouseLeaveList}
              >
                {!!item?.parentItem?.link?.target &&
                item.parentItem.link?.target === "_blank" ? (
                  <a
                    href={item?.parentItem?.link?.url}
                    rel="noreferrer"
                    target="_blank"
                    className={s.listedParentItem}
                  >
                    {item?.parentItem?.link?.title}
                  </a>
                ) : (
                  <Link
                    to={item?.parentItem?.link?.url}
                    className={s.listedParentItem}
                  >
                    {item?.parentItem?.link?.title}
                  </Link>
                )}
                {item?.hierarchyDepth === "two" && (
                  <ul className={s.listedItemSubmenu}>
                    {item?.childItems.map((childItem, childKey) => (
                      <li
                        className={`${s.listChildItem} ${
                          activeChildItem === childKey ? s.active : ""
                        }`}
                        key={childKey}
                        onMouseEnter={() => handleMouseEnterChild(childKey)}
                      >
                        {!!childItem?.link?.target &&
                        childItem?.link?.target === "_blank" ? (
                          <a
                            href={childItem?.link?.url}
                            rel="noreferrer"
                            target="_blank"
                            className={``}
                          >
                            {childItem?.link?.title}
                          </a>
                        ) : (
                          <Link
                            to={childItem?.link?.url}
                            className={s.listedParentItem}
                          >
                            {childItem?.link?.title}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {item?.hierarchyDepth === "three" && (
                  <ul
                    className={`${s.listedItemSubmenu} ${s.thirdLevelListedItemSubmenu}`}
                  >
                    {item?.childItemsThree.map((childItem, childKey) => {
                      return (
                        <li
                          className={`${
                            s.childItemOfThidryLevelParentElement
                          } ${childItem?.dontShowChild ? s.dontShowChild : ""}`}
                          key={childKey}
                        >
                          {!!childItem?.parentLink?.target &&
                          childItem?.parentLink?.target === "_blank" ? (
                            <a
                              href={childItem?.parentLink?.url}
                              rel="noreferrer"
                              target="_blank"
                              className={``}
                            >
                              {childItem?.parentLink?.title}
                            </a>
                          ) : (
                            <Link
                              to={childItem?.parentLink?.url}
                              className={s.listedParentItem}
                            >
                              {childItem?.parentLink?.title}
                            </Link>
                          )}
                          <ul className={`${s.thirdLevelSubmenu}`}>
                            {childItem?.childs.map((chItem, key) => {
                              return (
                                <li
                                  className={`${s.listChildItem} ${
                                    childKey == 0 && key == 0 ? s.active : ""
                                  }`}
                                  key={key}
                                  onMouseEnter={() => {
                                    setCurrentItem(chItem);
                                  }}
                                >
                                  {!!chItem?.link?.target &&
                                  chItem?.link?.target === "_blank" ? (
                                    <a
                                      href={chItem?.link?.url}
                                      rel="noreferrer"
                                      target="_blank"
                                      className={``}
                                    >
                                      {chItem?.link?.title}
                                    </a>
                                  ) : (
                                    <Link
                                      to={chItem?.link?.url}
                                      className={s.listedParentItem}
                                    >
                                      {chItem?.link?.title}
                                    </Link>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {item?.hierarchyDepth === "two" &&
                  !!item?.childItems?.length && (
                    <div style={{ padding: 0 }}>
                      <div className={s.childItemContent}>
                        <ItemContent
                          block={
                            item.childItems[activeChildItem || firstChildKey]
                              ?.itemContent
                          }
                        />
                      </div>
                    </div>
                  )}
                {item?.hierarchyDepth === "one" && (
                  <div style={{ padding: 0 }}>
                    <div className={s.ListParentItemContent}>
                      <ItemContent block={item?.itemContent} />
                    </div>
                  </div>
                )}
                {item?.hierarchyDepth === "three" && !!currentItem && (
                  <div style={{ padding: 0 }}>
                    <div className={s.childItemContent}>
                      <ItemContent block={currentItem?.itemContent} />
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className={s.divider}></div>
      <div className={s.staticNavItems}>
        <nav className={s.staticNavgation}>
          {navItems.map((item, key) => (
            <div
              className={`${s.navParentItem} ${
                null == activeItem &&
                null == activeListItem &&
                null == activeChildItem &&
                item?.parentItem?.showAsDefault
                  ? s.showDefault
                  : ""
              } ${activeItem === key ? s.active : ""}`}
              key={key}
              onMouseEnter={() => handleMouseEnterNav(key)}
              onMouseLeave={handleMouseLeaveNav}
            >
              {!!item?.parentItem?.link?.target &&
              item.parentItem.link.target === "_blank" ? (
                <a
                  href={item?.parentItem?.link?.url}
                  rel="noreferrer"
                  target="_blank"
                >
                  {item?.parentItem?.link?.title}
                </a>
              ) : (
                <Link to={item?.parentItem?.link?.url}>
                  {item?.parentItem?.link?.title}
                </Link>
              )}
              {!!item?.itemContent &&
                (activeItem === key || activeListItem === key) && (
                  <ItemContent block={item.itemContent} />
                )}
            </div>
          ))}
          {null == activeItem &&
            null == activeListItem &&
            null == activeChildItem &&
            !!defaultItem?.itemContent && (
              <div className={s.defaultWrapper}>
                <ItemContent block={defaultItem.itemContent} />
              </div>
            )}
        </nav>
      </div>
      <div className={s.divider}></div>
      <div className={s.socialWrapper}>
        <a
          href="https://www.instagram.com/the.houseoflife/"
          rel="noreferrer"
          target="_blank"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 2.52109C17.7406 2.52109 18.1836 2.5375 19.6547 2.60312C21.0219 2.66328 21.7602 2.89297 22.2523 3.08438C22.9031 3.33594 23.3734 3.64219 23.8602 4.12891C24.3523 4.62109 24.6531 5.08594 24.9047 5.73672C25.0961 6.22891 25.3258 6.97266 25.3859 8.33438C25.4516 9.81094 25.468 10.2539 25.468 13.9891C25.468 17.7297 25.4516 18.1727 25.3859 19.6438C25.3258 21.0109 25.0961 21.7492 24.9047 22.2414C24.6531 22.8922 24.3469 23.3625 23.8602 23.8492C23.368 24.3414 22.9031 24.6422 22.2523 24.8938C21.7602 25.0852 21.0164 25.3148 19.6547 25.375C18.1781 25.4406 17.7352 25.457 14 25.457C10.2594 25.457 9.81641 25.4406 8.34531 25.375C6.97813 25.3148 6.23984 25.0852 5.74766 24.8938C5.09688 24.6422 4.62656 24.3359 4.13984 23.8492C3.64766 23.357 3.34687 22.8922 3.09531 22.2414C2.90391 21.7492 2.67422 21.0055 2.61406 19.6438C2.54844 18.1672 2.53203 17.7242 2.53203 13.9891C2.53203 10.2484 2.54844 9.80547 2.61406 8.33438C2.67422 6.96719 2.90391 6.22891 3.09531 5.73672C3.34687 5.08594 3.65313 4.61563 4.13984 4.12891C4.63203 3.63672 5.09688 3.33594 5.74766 3.08438C6.23984 2.89297 6.98359 2.66328 8.34531 2.60312C9.81641 2.5375 10.2594 2.52109 14 2.52109ZM14 0C10.1992 0 9.72344 0.0164062 8.23047 0.0820313C6.74297 0.147656 5.72031 0.388281 4.83437 0.732813C3.91016 1.09375 3.12813 1.56953 2.35156 2.35156C1.56953 3.12813 1.09375 3.91016 0.732812 4.82891C0.388281 5.72031 0.147656 6.7375 0.0820313 8.225C0.0164063 9.72344 0 10.1992 0 14C0 17.8008 0.0164063 18.2766 0.0820313 19.7695C0.147656 21.257 0.388281 22.2797 0.732812 23.1656C1.09375 24.0898 1.56953 24.8719 2.35156 25.6484C3.12813 26.425 3.91016 26.9063 4.82891 27.2617C5.72031 27.6063 6.7375 27.8469 8.225 27.9125C9.71797 27.9781 10.1937 27.9945 13.9945 27.9945C17.7953 27.9945 18.2711 27.9781 19.7641 27.9125C21.2516 27.8469 22.2742 27.6063 23.1602 27.2617C24.0789 26.9063 24.8609 26.425 25.6375 25.6484C26.4141 24.8719 26.8953 24.0898 27.2508 23.1711C27.5953 22.2797 27.8359 21.2625 27.9016 19.775C27.9672 18.282 27.9836 17.8063 27.9836 14.0055C27.9836 10.2047 27.9672 9.72891 27.9016 8.23594C27.8359 6.74844 27.5953 5.72578 27.2508 4.83984C26.9062 3.91016 26.4305 3.12813 25.6484 2.35156C24.8719 1.575 24.0898 1.09375 23.1711 0.738281C22.2797 0.39375 21.2625 0.153125 19.775 0.0875C18.2766 0.0164063 17.8008 0 14 0Z"
              fill="white"
            />
            <path
              d="M14 6.80859C10.0297 6.80859 6.80859 10.0297 6.80859 14C6.80859 17.9703 10.0297 21.1914 14 21.1914C17.9703 21.1914 21.1914 17.9703 21.1914 14C21.1914 10.0297 17.9703 6.80859 14 6.80859ZM14 18.6648C11.4242 18.6648 9.33516 16.5758 9.33516 14C9.33516 11.4242 11.4242 9.33516 14 9.33516C16.5758 9.33516 18.6648 11.4242 18.6648 14C18.6648 16.5758 16.5758 18.6648 14 18.6648Z"
              fill="white"
            />
            <path
              d="M23.1547 6.52417C23.1547 7.45386 22.4 8.20308 21.4758 8.20308C20.5461 8.20308 19.7969 7.44839 19.7969 6.52417C19.7969 5.59448 20.5516 4.84526 21.4758 4.84526C22.4 4.84526 23.1547 5.59995 23.1547 6.52417Z"
              fill="white"
            />
          </svg>
        </a>
        <a
          href="https://www.facebook.com/firsthouseoflife"
          rel="noreferrer"
          target="_blank"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_114_1144)">
              <path
                d="M28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 20.9877 5.11957 26.7796 11.8125 27.8299V18.0469H8.25781V14H11.8125V10.9156C11.8125 7.40688 13.9027 5.46875 17.1005 5.46875C18.6318 5.46875 20.2344 5.74219 20.2344 5.74219V9.1875H18.4691C16.73 9.1875 16.1875 10.2668 16.1875 11.375V14H20.0703L19.4496 18.0469H16.1875V27.8299C22.8804 26.7796 28 20.9877 28 14Z"
                fill="white"
              />
            </g>
            <defs>
              <clipPath id="clip0_114_1144">
                <rect width="28" height="28" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </a>
        <a
          href="https://www.linkedin.com/company/houseoflife"
          rel="noreferrer"
          target="_blank"
        >
          <svg
            width="28"
            height="28"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
          >
            <path
              d="M100.3 448H7.4V148.9h92.9zM53.8 108.1C24.1 108.1 0 83.5 0 53.8a53.8 53.8 0 0 1 107.6 0c0 29.7-24.1 54.3-53.8 54.3zM447.9 448h-92.7V302.4c0-34.7-.7-79.2-48.3-79.2-48.3 0-55.7 37.7-55.7 76.7V448h-92.8V148.9h89.1v40.8h1.3c12.4-23.5 42.7-48.3 87.9-48.3 94 0 111.3 61.9 111.3 142.3V448z"
              fill="white"
            />
          </svg>
        </a>
        <a
          href="https://youtube.com/@1sthouseoflife"
          rel="noreferrer"
          target="_blank"
        >
          <svg
            width="28"
            height="28"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 576 512"
          >
            <path
              d="M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6-11.4 42.9-11.4 132.3-11.4 132.3s0 89.4 11.4 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.2 48.3-47.8 11.4-42.9 11.4-132.3 11.4-132.3s0-89.4-11.4-132.3zm-317.5 213.5V175.2l142.7 81.2-142.7 81.2z"
              fill="white"
            />
          </svg>
        </a>
      </div>
    </div>
  );
};
